import { formatPrice } from '../format'
import type { VolumePricingCurve } from '../types'
import { VolumeCurveLegend } from './VolumeCurveVisual'

export interface AutoPricedCurveCardProps {
  readonly curve: VolumePricingCurve
  readonly currency: string
}

const CHART_WIDTH = 640
// dc.html's own SVG viewBox is 560×196 (aspect ~2.86:1). At the old 640×168
// (~3.8:1) the chart read too short/flat once it actually spanned the full
// card width (the prior full-width fix). Keeping `CHART_WIDTH` at 640 and
// scaling `CHART_HEIGHT` to the SAME ~2.86:1 aspect (640 / 2.86 ≈ 224)
// matches the prototype's proportions without needing to also re-derive
// every other constant's absolute value from scratch.
const CHART_HEIGHT = 224
// A hair of padding only — not the old 46px gutter that used to reserve
// room for y-axis price labels. Those labels were removed (UID-332 final
// decorative-chart pass); keeping a large left inset after that just reads
// as wasted whitespace instead of a full-width chart like the prototype's.
// A few px keeps the qty-1 marker's circle from clipping the SVG edge.
const X_LEFT = 6
const X_RIGHT = CHART_WIDTH - 6
// Y_TOP/X_AXIS_GAP scaled by the same ~1.333 factor as CHART_HEIGHT
// (224/168) so the plot's proportions stay balanced in the taller box,
// rather than just adding the extra height entirely to the bottom margin.
const Y_TOP = 21
// Vertical room reserved below the plot (no x-axis labels render anymore,
// but the plot keeps a small bottom margin so the cap-line/markers aren't
// flush against the SVG's edge).
const X_AXIS_GAP = 19

const SOLID_SAMPLES = 48
const DASH_SAMPLES = 18

/**
 * The Part-detail auto-priced curve visualization (UID-332 final pass,
 * `Fictiv Platform.dc.html:1722-1735` / `costCurve` at `:6314-6334`) — a
 * PURELY DECORATIVE chart matching the prototype's clean image: a single
 * smooth convex curve, exactly 3 hollow markers (qty 1 / 50 / cap), no text
 * inside the chart at all (no per-marker price, no axis labels/ticks).
 *
 * "Decorative" here means the CURVE SHAPE is generated from a small
 * power-law formula seeded only by `base` (the smallest-qty tier's real
 * unit price) and `cap` — it is NOT sampled/interpolated through the real
 * per-tier prices the way the previous iteration was. The real tiers are
 * never altered and never disappear: they stay fully available, verbatim,
 * in the `<ul className="sr-only">` list below the chart, which is the
 * ONLY place a screen reader (or anyone reading the DOM) sees real prices
 * for real quantities. The chart is `aria-hidden` and carries an explicit
 * sr-only note pointing back to that real list (WCAG 1.1.1/1.4.1) — same
 * "decorative chart + real accessible list" split this vertical already
 * uses for `VolumeCurveChart`/`VolumeBreaksList`.
 *
 * The pricing-card HEADER above this component (headline range, "Request
 * volume RFQ" CTA) is unaffected — it still reads the real tiers directly
 * in `PartDetailPage.tsx`, not anything from this file.
 */
export function AutoPricedCurveCard({ curve, currency }: AutoPricedCurveCardProps) {
  const tiers = [...curve.tiers].sort((a, b) => a.minQuantity - b.minQuantity)
  if (tiers.length === 0) return null

  const cap = curve.cap
  // `base` = the qty-1 (smallest-quantity) real tier's unit price — the ONE
  // real number this decorative shape is seeded from, per spec. Everything
  // else below (`hi`/`lo`/the sampled curve) is an illustrative shape, not
  // a plot of the real per-tier data.
  const base = Number.parseFloat(tiers[0]?.unitPriceAmount ?? '0')
  const plotBottom = CHART_HEIGHT - X_AXIS_GAP

  // LOG x-scale (`costCurve`, dc.html:6314-6334) — puts the cap at ~87% of
  // the plot width and gives the "beyond cap" band room past it.
  const axMax = cap * 2.4
  function xFor(qty: number): number {
    if (axMax <= 1) return X_LEFT
    const t = Math.log(Math.max(1, qty)) / Math.log(axMax)
    return X_LEFT + t * (X_RIGHT - X_LEFT)
  }

  // Decorative power-law price shape (`costCurve`), seeded only by `base`
  // and `cap` — NOT the real per-tier prices.
  const r = 0.5
  const hi = base * 1.35
  const lo = base * 0.72
  const capR = cap ** r
  function unitAt(qty: number): number {
    if (qty >= cap || capR === 1) return lo
    return lo + (hi - lo) * ((capR - qty ** r) / (capR - 1))
  }

  // y-band, padded like the prototype so the curve's top/bottom never sit
  // flush against the plot edges.
  const yBandLow = lo * 0.82
  const yBandHigh = hi * 1.08
  function yFor(unit: number): number {
    if (yBandHigh === yBandLow) return (Y_TOP + plotBottom) / 2
    const t = (unit - yBandLow) / (yBandHigh - yBandLow)
    return Y_TOP + (1 - t) * (plotBottom - Y_TOP)
  }

  // Solid curve: sample `unitAt` at SOLID_SAMPLES points, LOG-spaced from
  // qty=1 to qty=cap (matching the log x-scale so the samples land evenly
  // across the rendered width) — dense enough to read as one smooth,
  // continuously convex curve without any interpolation library.
  const solidPoints: { x: number; y: number }[] = []
  for (let i = 0; i < SOLID_SAMPLES; i++) {
    const t = i / (SOLID_SAMPLES - 1)
    const qty = Math.exp(t * Math.log(Math.max(cap, 1)))
    solidPoints.push({ x: xFor(qty), y: yFor(unitAt(qty)) })
  }
  const firstSolid = solidPoints[0]
  const lastSolid = solidPoints[solidPoints.length - 1]
  const linePath = solidPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ')
  const areaPath =
    firstSolid && lastSolid
      ? `${linePath} L${lastSolid.x},${plotBottom} L${firstSolid.x},${plotBottom} Z`
      : ''

  const capX = xFor(cap)
  const capY = yFor(lo)
  // Dashed "RFQ beyond" continuation: flat at `lo` (the price never drops
  // further past the cap in this shape) from the cap out to the domain
  // edge — sampled the same log-spaced way for consistency, even though the
  // flat price makes every sample land on one horizontal line.
  const dashPoints: { x: number; y: number }[] = []
  for (let i = 0; i < DASH_SAMPLES; i++) {
    const t = i / (DASH_SAMPLES - 1)
    const qty = cap * (axMax / cap) ** t
    dashPoints.push({ x: xFor(qty), y: capY })
  }
  const dashPath = dashPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ')

  // Exactly 3 markers — qty 1 / 50 / cap — deduped if `cap < 50` (a tiny
  // cap collapses the "50" marker onto or past the cap marker).
  const markerQtys = [...new Set([1, ...(50 < cap ? [50] : []), cap])]
  const markers = markerQtys.map((qty) => ({ qty, x: xFor(qty), y: yFor(unitAt(qty)) }))

  // 3 gridlines, no labels — at the shape's own hi / mid / lo band.
  const gridlineYs = [yFor(hi), yFor((hi + lo) / 2), yFor(lo)]

  return (
    <div className="auto-priced-curve">
      <div className="auto-priced-curve-header">
        <span className="auto-priced-curve-title">Unit price by quantity</span>
        <VolumeCurveLegend cap={cap} />
      </div>
      <svg
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        className="auto-priced-curve-chart"
        aria-hidden="true"
        focusable="false"
      >
        {gridlineYs.map((y) => (
          <line
            key={y}
            x1={X_LEFT}
            x2={X_RIGHT}
            y1={y}
            y2={y}
            className="auto-priced-curve-gridline"
          />
        ))}
        <rect
          x={capX}
          y={0}
          width={Math.max(X_RIGHT - capX, 0)}
          height={plotBottom}
          className="auto-priced-curve-beyond-rect"
        />
        <line x1={capX} x2={capX} y1={0} y2={plotBottom} className="auto-priced-curve-cap-line" />
        <path d={areaPath} className="auto-priced-curve-area" />
        <path d={linePath} className="auto-priced-curve-line" />
        <path d={dashPath} className="auto-priced-curve-dash" />
        {markers.map((m) => (
          <circle key={m.qty} cx={m.x} cy={m.y} r={5.5} className="auto-priced-curve-marker" />
        ))}
      </svg>
      <p className="sr-only">
        Illustrative price-vs-quantity curve; see the tier list below for exact prices.
      </p>
      <ul className="sr-only">
        {tiers.map((tier) => (
          <li key={tier.minQuantity}>
            Qty {tier.minQuantity}: {formatPrice({ amount: tier.unitPriceAmount, currency })} per
            unit
          </li>
        ))}
      </ul>
      <p className="caption-text auto-priced-curve-caption">
        Above {cap} pcs we can't auto-price — request an RFQ for committed volume pricing.
      </p>
    </div>
  )
}
